import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiPlus, FiSearch, FiX, FiArrowRight, FiPrinter,
  FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight,
  FiArchive, FiRefreshCw, FiMinus
} from 'react-icons/fi';
import {
  getConversions, createConversion, anularConversion,
  getWarehouses, getWarehouseStock, getProducts
} from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import '../../../styles/ModuleTable.css';
import './ConversionesMaintenance.css';

/* ── Helpers ─────────────────────────────────────────────────────────── */
function fmtDt(str) {
  if (!str) return '-';
  const d = new Date(str);
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth()+1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
function fmtQ(n) {
  return parseFloat(n ?? 0).toFixed(3).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

const ITEMS = [10, 20, 50];

/* ── Impresión de comprobante ─────────────────────────────────────────── */
function imprimirConversion(conv, businessName = 'PIVASAPOLLOS') {
  const win = window.open('', '_blank', 'width=750,height=650');
  const filasProd = (list, color) => list.map(p => `
    <tr>
      <td>${p.sku}</td>
      <td>${p.product_name}</td>
      <td>${p.unit_measure || '—'}</td>
      <td style="text-align:right;font-weight:700;color:${color}">${fmtQ(p.quantity)}</td>
      ${p.special_condition ? `<td style="text-align:right;color:${color}">${p.qty_unidad || 0}</td>` : '<td>—</td>'}
    </tr>`).join('');

  // Totales de productos obtenidos, agrupados por unidad de medida (kg, l, etc.)
  // más el total de unidades (UND) de los productos de doble variable.
  const outputs = conv.outputs || [];
  const totalsByUnit = {};
  let totalUnidades = 0;
  outputs.forEach(p => {
    const um = p.unit_measure || 'UM';
    totalsByUnit[um] = (totalsByUnit[um] || 0) + parseFloat(p.quantity || 0);
    if (p.special_condition) totalUnidades += parseInt(p.qty_unidad || 0, 10);
  });
  const totalsByUnitTxt = Object.entries(totalsByUnit)
    .map(([um, val]) => `${fmtQ(val)} ${um}`)
    .join(' + ') || '0';
  const filaTotalOutputs = outputs.length ? `
    <tr class="tot-row">
      <td colspan="3" style="text-align:right;font-weight:800">TOTAL PRODUCTOS OBTENIDOS:</td>
      <td style="text-align:right;font-weight:800;color:#16a34a">${totalsByUnitTxt}</td>
      <td style="text-align:right;font-weight:800;color:#16a34a">${totalUnidades > 0 ? `${totalUnidades} UND` : '—'}</td>
    </tr>` : '';

  win.document.write(`<!DOCTYPE html>
<html lang="es"><head><meta charset="UTF-8"/>
<title>Conversión ${conv.conversion_number}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:12px;color:#1e293b;padding:24px 32px}
  .header{text-align:center;margin-bottom:20px;border-bottom:2px solid #0d1b2e;padding-bottom:14px}
  .co-name{font-size:18px;font-weight:800;color:#0d1b2e;letter-spacing:1px}
  .doc-title{font-size:13px;font-weight:700;color:#0369a1;margin-top:6px;letter-spacing:2px;text-transform:uppercase}
  .doc-number{font-size:22px;font-weight:900;color:#0d1b2e;margin-top:4px}
  .meta{display:flex;justify-content:space-between;margin-bottom:16px;font-size:11.5px}
  .meta-block{display:flex;flex-direction:column;gap:3px}
  .meta-label{font-weight:700;color:#64748b;text-transform:uppercase;font-size:10px;letter-spacing:0.5px}
  .meta-val{color:#1e293b;font-weight:600}
  .section-title{font-size:10.5px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;border-bottom:1px solid #e2e8f0;padding-bottom:4px;margin:14px 0 8px}
  .section-title.in{border-left:4px solid #dc2626;padding-left:8px}
  .section-title.out{border-left:4px solid #16a34a;padding-left:8px}
  table{width:100%;border-collapse:collapse;margin-bottom:14px}
  th{background:#0d1b2e;color:#e2e8f0;padding:7px 10px;text-align:left;font-size:11px;letter-spacing:0.3px}
  td{padding:7px 10px;border-bottom:1px solid #e2e8f0;font-size:12px}
  tr:nth-child(even) td{background:#f8fafc}
  .tot-row td{background:#f0fdf4;border-top:2px solid #16a34a;border-bottom:none}
  .obs-box{background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:10px 14px;font-size:12px;color:#475569;margin-bottom:16px;min-height:32px}
  .firmas{display:flex;gap:40px;margin-top:32px}
  .firma-box{flex:1;border-top:1px solid #94a3b8;padding-top:8px;text-align:center}
  .firma-label{font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:0.5px}
  .footer{margin-top:18px;text-align:center;font-size:10px;color:#94a3b8;border-top:1px solid #e2e8f0;padding-top:10px}
</style></head><body>
<div class="header">
  <div class="co-name">${businessName}</div>
  <div class="doc-title">Comprobante de Conversión de Inventario</div>
  <div class="doc-number">${conv.conversion_number}</div>
</div>
<div class="meta">
  <div class="meta-block"><span class="meta-label">Fecha</span><span class="meta-val">${fmtDt(conv.created_at)}</span></div>
  <div class="meta-block"><span class="meta-label">Almacén</span><span class="meta-val">${conv.warehouse_name}</span></div>
  <div class="meta-block"><span class="meta-label">Usuario</span><span class="meta-val">${conv.username || '—'}</span></div>
</div>
<div class="section-title in">PRODUCTOS CONSUMIDOS (Entradas al proceso)</div>
<table>
  <thead><tr><th>Cód.</th><th>Producto</th><th>U.M</th><th style="text-align:right">Cantidad</th><th style="text-align:right">Uds.</th></tr></thead>
  <tbody>${filasProd(conv.inputs || [], '#dc2626')}</tbody>
</table>
<div class="section-title out">PRODUCTOS OBTENIDOS (Salidas del proceso)</div>
<table>
  <thead><tr><th>Cód.</th><th>Producto</th><th>U.M</th><th style="text-align:right">Cantidad</th><th style="text-align:right">Uds.</th></tr></thead>
  <tbody>${filasProd(conv.outputs || [], '#16a34a')}</tbody>
  <tfoot>${filaTotalOutputs}</tfoot>
</table>
<div class="section-title">Observaciones</div>
<div class="obs-box">${conv.notes || 'Sin observaciones'}</div>
<div class="firmas">
  <div class="firma-box"><div class="firma-label">Responsable de proceso</div></div>
  <div class="firma-box"><div class="firma-label">Supervisor</div></div>
  <div class="firma-box"><div class="firma-label">Almacenero</div></div>
</div>
<div class="footer">PIVASAPOLLOS — Generado ${new Date().toLocaleString('es-PE', { timeZone: 'America/Lima' })} — ${conv.conversion_number}</div>
<script>window.onload=()=>{window.print();}</script>
</body></html>`);
  win.document.close();
}

/* ── Paginación ─────────────────────────────────────────────────────── */
function Paginacion({ page, totalPages, perPage, setPage, setPerPage }) {
  return (
    <div className="mt-pagination">
      <span className="pg-info">({page} de {totalPages})</span>
      <button onClick={() => setPage(1)} disabled={page === 1}><FiChevronsLeft size={12} /></button>
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}><FiChevronLeft size={12} /></button>
      <span className="pg-current">{page}</span>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}><FiChevronRight size={12} /></button>
      <button onClick={() => setPage(totalPages)} disabled={page === totalPages}><FiChevronsRight size={12} /></button>
      <select value={perPage} onChange={e => { setPerPage(+e.target.value); setPage(1); }}>
        {ITEMS.map(n => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
  );
}

/* ── Selector de producto con búsqueda ───────────────────────────────── */
function ProdSelector({ businessId, warehouseId, onlyWarehouse = false, onSelect, placeholder }) {
  const [search, setSearch] = useState('');
  const [list, setList]     = useState([]);

  useEffect(() => {
    if (!businessId) return;
    if (onlyWarehouse && !warehouseId) { setList([]); return; }
    const load = async () => {
      try {
        if (onlyWarehouse) {
          const r = await getWarehouseStock(warehouseId, businessId, { search });
          setList(r.data?.data || []);
        } else {
          const r = await getProducts(businessId, search);
          setList(r.data?.data || []);
        }
      } catch { setList([]); }
    };
    load();
  }, [businessId, warehouseId, search, onlyWarehouse]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return list.filter(p =>
      p.name?.toLowerCase().includes(q) ||
      p.sku?.toLowerCase().includes(q)
    );
  }, [list, search]);

  return (
    <div>
      <div className="cnv-prod-search">
        <FiSearch size={12} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={placeholder || 'Buscar producto...'}
        />
        {search && <button className="mov-clear-btn" onClick={() => setSearch('')}><FiX size={11} /></button>}
      </div>
      <div className="cnv-prod-list">
        {filtered.length === 0
          ? <div className="cnv-prod-empty">{onlyWarehouse && !warehouseId ? 'Selecciona un almacén' : 'Sin resultados'}</div>
          : filtered.slice(0, 12).map(p => {
              const pid  = p.product_id || p.id;
              const stk  = parseFloat(p.stock || 0);
              return (
                <div key={pid} className="cnv-prod-item" onClick={() => { onSelect(p); setSearch(''); }}>
                  <span className="cnv-prod-sku">{p.sku}</span>
                  <span className="cnv-prod-name">{p.name}</span>
                  <span className={`cnv-prod-stk ${stk <= 0 ? 'zero' : ''}`}>
                    {fmtQ(stk)} {p.unit_measure}
                  </span>
                </div>
              );
            })
        }
      </div>
    </div>
  );
}

/* ── Lista de items (input o output) ─────────────────────────────────── */
function ItemsList({ items, onQtyChange, onUniChange, onRemove, isInput }) {
  if (!items.length) {
    return <div className="cnv-empty-items">{isInput ? 'Agrega productos a consumir' : 'Agrega productos a producir'}</div>;
  }
  return (
    <div className="cnv-items-list">
      {items.map((item, i) => (
        <div key={i} className={`cnv-item-row ${isInput ? 'is-input' : 'is-output'}`}>
          <span className="cnv-item-name" title={item.name}>{item.sku} — {item.name}</span>
          <div className="cnv-item-qty">
            <span className="cnv-item-field-lbl">Cant. ({item.unit_measure || 'UM'})</span>
            <input
              type="number" step="0.001" min="0.001"
              value={item.quantity}
              onChange={e => onQtyChange(i, e.target.value)}
              title={`Cantidad (${item.unit_measure})`}
            />
          </div>
          {item.special_condition && (
            <div className="cnv-item-uni">
              <span className="cnv-item-field-lbl">Uds. (UND)</span>
              <input
                type="number" step="1" min="0"
                value={item.qty_unidad}
                onChange={e => onUniChange(i, e.target.value)}
                title="Unidades (UND)"
              />
            </div>
          )}
          {!item.special_condition && <div />}
          <button className="cnv-item-del" onClick={() => onRemove(i)} title="Quitar"><FiX size={13} /></button>
          <span className="cnv-item-hint">
            {isInput
              ? `Disp: ${fmtQ(item.stock)} ${item.unit_measure}${item.special_condition ? ` / ${item.stock_unidad} uds` : ''}`
              : item.unit_measure
            }
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── Modal Nueva Conversión ──────────────────────────────────────────── */
function ModalNueva({ businessId, warehouses, onClose, onSaved }) {
  const [whId, setWhId]     = useState(warehouses[0]?.id || '');
  const [inputs, setInputs] = useState([]);
  const [outputs, setOutputs] = useState([]);
  const [notas, setNotas]   = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  function addItem(list, setList, prod) {
    const pid = prod.product_id || prod.id;
    if (list.find(i => (i.product_id || i.id) === pid)) return;
    setList(prev => [...prev, {
      product_id:       pid,
      id:               pid,
      sku:              prod.sku,
      name:             prod.name,
      unit_measure:     prod.unit_measure,
      special_condition: !!prod.special_condition,
      stock:            prod.stock || 0,
      stock_unidad:     prod.stock_unidad || 0,
      quantity:         '',
      qty_unidad:       0,
    }]);
  }

  function updateQty(list, setList, i, val) {
    setList(prev => prev.map((it, idx) => idx === i ? { ...it, quantity: val } : it));
  }
  function updateUni(list, setList, i, val) {
    setList(prev => prev.map((it, idx) => idx === i ? { ...it, qty_unidad: val } : it));
  }
  function removeItem(list, setList, i) {
    setList(prev => prev.filter((_, idx) => idx !== i));
  }

  async function handleSave() {
    setError('');
    if (!whId) { setError('Selecciona el almacén.'); return; }
    if (!inputs.length)  { setError('Agrega al menos un producto a consumir.'); return; }
    if (!outputs.length) { setError('Agrega al menos un producto a producir.'); return; }
    for (const it of inputs)  { if (!parseFloat(it.quantity) > 0) { setError(`Cantidad inválida en consumo: ${it.name}`); return; } }
    for (const it of outputs) { if (!parseFloat(it.quantity) > 0) { setError(`Cantidad inválida en producción: ${it.name}`); return; } }

    setSaving(true);
    try {
      const res = await createConversion(businessId, {
        warehouse_id: whId,
        inputs:  inputs.map(i  => ({ product_id: i.product_id || i.id, quantity: parseFloat(i.quantity),  qty_unidad: parseInt(i.qty_unidad  || 0) })),
        outputs: outputs.map(o => ({ product_id: o.product_id || o.id, quantity: parseFloat(o.quantity), qty_unidad: parseInt(o.qty_unidad || 0) })),
        notes: notas.trim() || null,
      });
      onSaved(res.data.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  }

  return (
    <div className="mt-modal-overlay">
      <div className="mt-modal cnv-modal">
        <div className="mt-modal-header">
          <span><FiRefreshCw size={14} style={{ marginRight: 6 }} />Nueva Conversión de Inventario</span>
          <button className="btn-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mt-modal-body">

          {/* Almacén */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 4 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                <FiArchive size={11} style={{ marginRight: 4 }} />Almacén del proceso <span className="req">*</span>
              </label>
              <select
                value={whId}
                onChange={e => { setWhId(e.target.value); setInputs([]); setOutputs([]); }}
                style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, minWidth: 200 }}
              >
                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
          </div>

          {/* Panel split: consumo / producción */}
          <div className="cnv-panels">

            {/* INPUTS — productos consumidos */}
            <div>
              <div className="cnv-section-title">
                <FiMinus size={12} /> Consumir <span className="pill-in">ENTRADA AL PROCESO</span>
              </div>
              <ProdSelector
                businessId={businessId}
                warehouseId={whId}
                onlyWarehouse={true}
                placeholder="Buscar producto del almacén..."
                onSelect={p => addItem(inputs, setInputs, p)}
              />
              <ItemsList
                items={inputs}
                onQtyChange={(i, v) => updateQty(inputs, setInputs, i, v)}
                onUniChange={(i, v) => updateUni(inputs, setInputs, i, v)}
                onRemove={i => removeItem(inputs, setInputs, i)}
                isInput={true}
              />
            </div>

            {/* OUTPUTS — productos producidos */}
            <div>
              <div className="cnv-section-title">
                <FiPlus size={12} /> Producir <span className="pill-out">SALIDA DEL PROCESO</span>
              </div>
              <ProdSelector
                businessId={businessId}
                warehouseId={whId}
                onlyWarehouse={false}
                placeholder="Buscar producto a producir..."
                onSelect={p => addItem(outputs, setOutputs, p)}
              />
              <ItemsList
                items={outputs}
                onQtyChange={(i, v) => updateQty(outputs, setOutputs, i, v)}
                onUniChange={(i, v) => updateUni(outputs, setOutputs, i, v)}
                onRemove={i => removeItem(outputs, setOutputs, i)}
                isInput={false}
              />
            </div>
          </div>

          {/* Observaciones */}
          <div style={{ marginTop: 14 }}>
            <div className="cnv-section-title">Observaciones</div>
            <textarea
              className="cnv-notas"
              value={notas}
              onChange={e => setNotas(e.target.value)}
              placeholder="Descripción del proceso de conversión, motivo, observaciones..."
            />
          </div>

          {error && <div className="cnv-error">{error}</div>}
        </div>

        <div className="mt-modal-footer">
          <button className="btn-modificar" onClick={handleSave} disabled={saving}>
            {saving ? 'Procesando...' : 'Registrar Conversión'}
          </button>
          <button className="btn-salir" onClick={onClose}>✕ Salir</button>
        </div>
      </div>
    </div>
  );
}

/* ── Modal resultado post-guardado ───────────────────────────────────── */
function ModalResultado({ conv, businessName, onClose, onNueva }) {
  return (
    <div className="mt-modal-overlay">
      <div className="mt-modal cnv-result-modal">
        <div className="mt-modal-header">
          <span>Conversión registrada</span>
          <button className="btn-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mt-modal-body" style={{ padding: '24px 20px', textAlign: 'center' }}>
          <div className="cnv-result-icon">✅</div>
          <div className="cnv-result-number">{conv.conversion_number}</div>
          <div className="cnv-result-detail">
            <strong>{conv.inputs?.length || 0}</strong> productos consumidos →{' '}
            <strong>{conv.outputs?.length || 0}</strong> productos producidos<br />
            <span style={{ color: '#94a3b8', fontSize: 11.5 }}>Almacén: {conv.warehouse_name}</span>
          </div>
          <div className="cnv-result-actions">
            <button className="btn-cnv-imprimir" onClick={() => imprimirConversion(conv, businessName)}>
              <FiPrinter size={14} /> Imprimir comprobante
            </button>
            <button className="btn-cnv-cerrar" onClick={onNueva}>
              <FiPlus size={13} /> Nueva conversión
            </button>
            <button className="btn-cnv-cerrar" onClick={onClose}>Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Componente principal ─────────────────────────────────────────────── */
export default function ConversionesMaintenance({ businessId }) {
  const { user } = useAuth();
  const businessName = user?.business_name || 'PIVASAPOLLOS';

  const [data, setData]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [warehouses, setWH]     = useState([]);
  const [page, setPage]         = useState(1);
  const [perPage, setPerPage]   = useState(10);
  const [modal, setModal]       = useState(false);
  const [resultado, setResultado] = useState(null);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]     = useState('');
  const [whFilter, setWhFilter] = useState('');
  const [search, setSearch]     = useState('');

  useEffect(() => {
    getWarehouses(businessId).then(r => setWH(r.data?.data || [])).catch(() => setWH([]));
  }, [businessId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getConversions(businessId, {
        from_date:    fromDate || undefined,
        to_date:      toDate   || undefined,
        warehouse_id: whFilter || undefined,
        search:       search   || undefined,
        page, limit: perPage,
      });
      setData(res.data?.data  || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      alert('Error al cargar conversiones: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [businessId, fromDate, toDate, whFilter, search, page, perPage]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  async function handleAnular(row) {
    if (!window.confirm(`¿Anular la conversión ${row.conversion_number}?\nSe revertirá el stock de todos los productos. El registro quedará como ANULADO.`)) return;
    try {
      await anularConversion(row.id, businessId);
      load();
    } catch (e) {
      alert('Error al anular: ' + (e.response?.data?.error || e.message));
    }
  }

  function handleSaved(conv) { setModal(false); setResultado(conv); load(); }
  function handleNueva()     { setResultado(null); setModal(true); }

  return (
    <div className="mt-container">
      {/* Toolbar */}
      <div className="mt-toolbar">
        <div className="mt-toolbar-left">
          <button className="btn-nuevo" onClick={() => setModal(true)}>
            <FiPlus size={12} /> Nueva Conversión
          </button>
        </div>
        <div className="mt-search-global">
          <span>Búsqueda</span>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="N° conversión, notas..."
          />
        </div>
      </div>

      {/* Filtros */}
      <div className="cnv-filters">
        <div className="cnv-filter-group">
          <label>Desde</label>
          <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPage(1); }} />
        </div>
        <div className="cnv-filter-group">
          <label>Hasta</label>
          <input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPage(1); }} />
        </div>
        <div className="cnv-filter-group">
          <label>Almacén</label>
          <select value={whFilter} onChange={e => { setWhFilter(e.target.value); setPage(1); }}>
            <option value="">Todos</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
        <div className="cnv-filter-group" style={{ marginLeft: 'auto' }}>
          <label>&nbsp;</label>
          <button
            style={{ padding: '6px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#475569' }}
            onClick={() => { setFromDate(''); setToDate(''); setWhFilter(''); setSearch(''); setPage(1); }}
          >
            Limpiar
          </button>
        </div>
      </div>

      {/* Resumen */}
      <div className="cnv-summary-bar">
        <FiRefreshCw size={13} />
        <span>Total conversiones:</span>
        <span className="cnv-summary-pill">{total}</span>
      </div>

      <div className="mt-export-bar">
        <Paginacion page={page} totalPages={totalPages} perPage={perPage} setPage={setPage} setPerPage={setPerPage} />
      </div>

      {/* Tabla */}
      <div className="mt-table-wrap">
        {loading
          ? <div className="mt-loading">Cargando...</div>
          : (
            <table className="mt-table mt-table-fixed">
              <colgroup>
                <col style={{ width: '11%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '21%' }} />
                <col style={{ width: '21%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '11%' }} />
              </colgroup>
              <thead>
                <tr className="mt-head-row">
                  <th>N° Conversión</th>
                  <th>Fecha</th>
                  <th>Almacén</th>
                  <th style={{ color: '#dc2626' }}>Consumido</th>
                  <th style={{ color: '#16a34a' }}>Producido</th>
                  <th>Acciones</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0
                  ? <tr className="mt-empty"><td colSpan={7}>Sin conversiones registradas</td></tr>
                  : data.map((row, i) => {
                    const anulado = !!row.anulado;
                    const rowCls  = anulado ? 'mt-row-anulado' : (i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd');
                    return (
                      <tr key={row.id} className={rowCls}>
                        <td className="no-strike"><span className="cnv-badge">{row.conversion_number}</span></td>
                        <td style={{ fontSize: 11.5 }}>{fmtDt(row.created_at)}</td>
                        <td className="no-strike" style={{ fontSize: 11.5 }}>
                          <span className="mov-warehouse-chip"><FiArchive size={10} />{row.warehouse_name}</span>
                        </td>
                        <td className="td-truncate" title={row.inputs_summary} style={{ fontSize: 11.5 }}>
                          {row.inputs_summary || '—'}{row.total_inputs > 1 ? ` (+${row.total_inputs - 1})` : ''}
                        </td>
                        <td className="td-truncate" title={row.outputs_summary} style={{ fontSize: 11.5 }}>
                          {row.outputs_summary || '—'}{row.total_outputs > 1 ? ` (+${row.total_outputs - 1})` : ''}
                        </td>
                        <td className="no-strike" style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                          <button
                            className="btn-cnv-print"
                            disabled={anulado}
                            onClick={async () => {
                              if (anulado) return;
                              try {
                                const { getConversionById } = await import('../../../services/api');
                                const r = await getConversionById(row.id, businessId);
                                imprimirConversion(r.data.data, businessName);
                              } catch { alert('Error al cargar detalle'); }
                            }}
                            title={anulado ? 'Conversión anulada' : 'Imprimir comprobante'}
                          >
                            <FiPrinter size={11} /> Imprimir
                          </button>
                          <button
                            className="btn-row-del"
                            disabled={anulado}
                            onClick={() => !anulado && handleAnular(row)}
                            title={anulado ? 'Ya anulada' : 'Anular conversión'}
                          >
                            <FiX size={13} />
                          </button>
                        </td>
                        <td className="no-strike">
                          {anulado
                            ? <span className="badge-estado-anulado">ANULADO</span>
                            : <span className="badge-estado-activo">ACTIVO</span>
                          }
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
          )
        }
      </div>

      <div className="mt-export-bar">
        <Paginacion page={page} totalPages={totalPages} perPage={perPage} setPage={setPage} setPerPage={setPerPage} />
      </div>

      {modal && (
        <ModalNueva
          businessId={businessId}
          warehouses={warehouses}
          onClose={() => setModal(false)}
          onSaved={handleSaved}
        />
      )}

      {resultado && (
        <ModalResultado
          conv={resultado}
          businessName={businessName}
          onClose={() => setResultado(null)}
          onNueva={handleNueva}
        />
      )}
    </div>
  );
}
